import React from 'react';
import { CheckCircle2, Circle, Truck, PackageCheck, PackageOpen, ClipboardCheck, MapPin, ExternalLink } from 'lucide-react';
import { useT, i18n } from '../services/i18n';
import { trackingUrl } from './TrackingForm';
import { carrierLogo } from '../services/carriers';

const RANK = { 'En attente': 0, 'En cours': 1, 'Expédié': 2, 'Livré': 3 };
const ICONS = { created: ClipboardCheck, processing: PackageOpen, shipped: Truck, delivered: PackageCheck };

const fmt = (d) => (d ? new Date(d).toLocaleString(i18n.locale(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : null);

const buildSteps = (order, t) => {
  const rank = RANK[order.status] ?? 0;
  const ev = (type) => (order.tracking_events || []).find((e) => e.type === type);
  const custom = (order.tracking_events || []).filter((e) => e.type === 'custom' || e.type === 'handed_over');
  const eta = order.shipped_at && !order.delivered_at ? new Date(new Date(order.shipped_at).getTime() + 3 * 86400000) : null;
  return [
    { key: 'created', done: true, at: order.created_at, desc: t('track.createdDesc') },
    { key: 'processing', done: rank >= 1, at: ev('processing')?.at, desc: t('track.processingDesc') },
    { key: 'shipped', done: rank >= 2, at: order.shipped_at || ev('shipped')?.at, desc: rank >= 2 ? t('track.shippedDesc') : t('track.noNumber'),
      extra: custom.map((e) => ({ id: e.id, label: e.type === 'handed_over' ? t('track.handed_over') : e.label, location: e.location, at: e.at })),
      eta: eta && rank === 2 ? eta : null },
    { key: 'delivered', done: rank >= 3, at: order.delivered_at || ev('delivered')?.at, desc: rank >= 3 ? t('track.deliveredDesc') : '' },
  ];
};

const OrderTrackingTimeline = ({ order, carriers = [] }) => {
  const { t } = useT();
  const steps = buildSteps(order, t);
  const link = order.tracking_number ? trackingUrl(carriers, order.tracking_carrier, order.tracking_number) : null;
  const carrierName = order.tracking_carrier || order.carrier_label || '';
  const logo = carrierLogo(carrierName);
  const lastAt = [...(order.tracking_events || [])].map((e) => e.at).concat([order.delivered_at, order.shipped_at, order.created_at]).filter(Boolean).sort().pop();

  return (
    <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 sm:p-5" data-testid="order-tracking-timeline">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Truck size={16} className="text-orange-500" /> {t('track.title')}
          {carrierName && <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-full pl-1 pr-2 py-0.5" data-testid="order-tracking-carrier">{logo && <img src={logo} alt="" className="h-5 w-12 object-contain no-placeholder" data-testid="order-tracking-carrier-logo" />}{carrierName}</span>}
        </h3>
        {lastAt && <span className="text-[11px] text-gray-400" data-testid="order-tracking-last-update">{t('track.lastUpdate', { d: fmt(lastAt) })}</span>}
      </div>
      <ol className="relative ml-3 border-l-2 border-gray-200 space-y-5">
        {steps.map((s, idx) => {
          const Icon = ICONS[s.key];
          const active = s.done && !steps[idx + 1]?.done;
          return (
            <li key={s.key} className="relative pl-7" data-testid={`track-step-${s.key}`} data-done={s.done ? '1' : '0'}>
              <span className={`absolute -left-[13px] top-0 flex h-6 w-6 items-center justify-center rounded-full border-2 bg-white ${s.done ? 'border-orange-500 text-orange-500' : 'border-gray-300 text-gray-300'} ${active ? 'ring-4 ring-orange-100' : ''}`}>
                {s.done ? <CheckCircle2 size={14} /> : <Circle size={10} />}
              </span>
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <p className={`text-sm font-semibold flex items-center gap-1.5 ${s.done ? 'text-gray-900' : 'text-gray-400'}`}><Icon size={14} /> {t(`track.${s.key}`)}</p>
                <span className="text-xs text-gray-400">{s.done ? fmt(s.at) : t('track.pending')}</span>
              </div>
              {s.desc && <p className={`text-xs mt-0.5 ${s.done ? 'text-gray-600' : 'text-gray-400'}`}>{s.desc}</p>}
              {s.eta && <p className="text-xs mt-1 text-orange-700 font-medium" data-testid="track-eta">{t('track.eta', { d: new Date(s.eta).toLocaleDateString(i18n.locale(), { weekday: 'long', day: 'numeric', month: 'long' }) })}</p>}
              {s.extra?.length > 0 && (
                <ul className="mt-2 space-y-1.5" data-testid="track-events">
                  {s.extra.map((e) => (
                    <li key={e.id} className="text-xs text-gray-700 flex items-start gap-1.5 bg-gray-50 rounded-md px-2 py-1.5" data-testid="track-event">
                      <MapPin size={12} className="text-blue-500 mt-0.5 shrink-0" />
                      <span><span className="font-medium">{e.label}</span>{e.location ? ` · ${e.location}` : ''} <span className="text-gray-400">— {fmt(e.at)}</span></span>
                    </li>
                  ))}
                </ul>
              )}
              {s.key === 'shipped' && order.tracking_number && (
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-mono bg-blue-50 text-blue-800 border border-blue-100 rounded px-2 py-1" data-testid="order-tracking-number">{logo && <img src={logo} alt="" className="h-4 w-8 object-contain no-placeholder" />}{order.tracking_carrier} · {order.tracking_number}</span>
                  {link && (
                    <a href={link} target="_blank" rel="noreferrer" data-testid="order-tracking-link" className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-md px-3 py-1.5 transition-colors">
                      {t('track.carrierLink', { carrier: order.tracking_carrier })} <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>
      <p className="text-[11px] text-gray-400 mt-4">{t('track.help')}</p>
    </div>
  );
};

export default OrderTrackingTimeline;
